import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { getProjectItems } from '../utils/api';

const TRADE_COLORS = {
  'Structural Steel': { color: 'var(--color-info)', bg: '#EFF6FF' },
  'Concrete':         { color: '#7C3AED', bg: '#F5F3FF' },
  'Masonry':          { color: '#92400E', bg: '#FFFBEB' },
  'Façade':           { color: '#0E7490', bg: '#ECFEFF' },
  'Roofing':          { color: '#5B21B6', bg: '#EDE9FE' },
  'Electrical':       { color: 'var(--color-warning)', bg: '#FFFBEB' },
  'HVAC':             { color: 'var(--color-teal)', bg: 'var(--color-teal-light)' },
  'Fire Protection':  { color: 'var(--color-error)', bg: '#FEF2F2' },
  'Groundworks':      { color: 'var(--color-info)', bg: '#EFF6FF' },
  'Finishes':         { color: '#6D28D9', bg: '#EDE9FE' },
  'Other':            { color: 'var(--color-text-mid)', bg: 'var(--color-background)' },
};

function ConfidencePill({ value }) {
  const n = Number(value);
  const color = n >= 90 ? 'var(--color-success)' : n >= 70 ? 'var(--color-warning)' : 'var(--color-error)';
  const bg = n >= 90 ? '#ECFDF5' : n >= 70 ? '#FFFBEB' : '#FEF2F2';
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 500, color, background: bg }}>{n >= 90 ? '✓' : n >= 70 ? '●' : '⚑'} {value}%</span>;
}

export default function Extract() {
  const { selectedProject } = useOutletContext();
  const [items, setItems] = useState([]);
  const [tradeFilter, setTradeFilter] = useState('All');
  const [confidenceFilter, setConfidenceFilter] = useState('All');
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadItems(selectedProject.id);
  }, [selectedProject]);

  async function loadItems(projectId) {
    setLoading(true);
    try {
      const data = await getProjectItems(projectId);
      setItems(data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  function toggleItem(id) { setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }

  const trades = ['All', ...new Set(items.map(i => i.trade_category || i.trade).filter(Boolean))];
  const filtered = items.filter(item => {
    const trade = item.trade_category || item.trade;
    const conf = Number(item.confidence) * (Number(item.confidence) <= 1 ? 100 : 1);
    const tradeMatch = tradeFilter === 'All' || trade === tradeFilter;
    const confMatch = confidenceFilter === 'All' ||
      (confidenceFilter === 'High' && conf >= 90) ||
      (confidenceFilter === 'Medium' && conf >= 70 && conf < 90) ||
      (confidenceFilter === 'Low' && conf < 70);
    return tradeMatch && confMatch;
  });

  const reviewItems = items.filter(i => Number(i.confidence) * (Number(i.confidence) <= 1 ? 100 : 1) < 70);
  const currentReviewItem = reviewItems[reviewIndex];

  if (reviewMode && currentReviewItem) {
    const conf = Number(currentReviewItem.confidence) * (Number(currentReviewItem.confidence) <= 1 ? 100 : 1);
    return (
      <div className="page">
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontWeight: 600 }}>Review item {reviewIndex + 1} of {reviewItems.length}</div>
            <button onClick={() => setReviewMode(false)} className="btn btn-ghost btn-sm">Exit review</button>
          </div>
          <div className="confidence-bar" style={{ marginBottom: 24 }}>
            <div className="confidence-bar-fill" style={{ width: ((reviewIndex + 1) / reviewItems.length * 100) + '%', background: 'var(--color-teal)' }} />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>{currentReviewItem.item_name || currentReviewItem.name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
                {[['Quantity', currentReviewItem.quantity], ['Unit', currentReviewItem.unit], ['Trade', currentReviewItem.trade_category || currentReviewItem.trade]].map(([l, v]) => (
                  <div key={l} style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--color-text-light)', marginBottom: 4 }}>{l}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{v}</div>
                  </div>
                ))}
              </div>
              <div className="risk-flag risk-flag-high">
                <span>⚑</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-error)' }}>Low confidence — {conf.toFixed(0)}%</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-mid)', marginTop: 2 }}>Review before proceeding</div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { if (reviewIndex < reviewItems.length - 1) setReviewIndex(i => i + 1); else setReviewMode(false); }} className="btn btn-primary" style={{ flex: 1 }}>Accept →</button>
            <button onClick={() => { if (reviewIndex < reviewItems.length - 1) setReviewIndex(i => i + 1); else setReviewMode(false); }} className="btn btn-secondary" style={{ flex: 1 }}>Edit item</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Extracted Items</div>
          <div className="page-subtitle">{filtered.length} items</div>
        </div>
        <div className="page-actions">
          <button onClick={() => {
            const csv = ['Description,Trade,Quantity,Unit,Confidence', ...items.map(i => `"${i.item_name || i.name}","${i.trade_category || i.trade}",${i.quantity},${i.unit},${i.confidence}`)].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'extracted-items.csv'; a.click();
          }} className="btn btn-secondary btn-sm">↓ Export CSV</button>
          {reviewItems.length > 0 && (
            <button onClick={() => { setReviewMode(true); setReviewIndex(0); }} className="btn btn-danger btn-sm">
              ⚑ Review ({reviewItems.length})
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: 'var(--color-text-light)', marginRight: 4 }}>Trade:</span>
        {trades.map(t => (
          <button key={t} onClick={() => setTradeFilter(t)} className="btn btn-sm" style={{ background: tradeFilter === t ? 'var(--color-teal)' : 'var(--color-surface)', color: tradeFilter === t ? '#fff' : 'var(--color-text-mid)', border: `1px solid ${tradeFilter === t ? 'var(--color-teal)' : 'var(--color-border)'}` }}>{t}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--color-border)', margin: '0 4px' }} />
        <span style={{ fontSize: 12, color: 'var(--color-text-light)', marginRight: 4 }}>Confidence:</span>
        {['All', 'High', 'Medium', 'Low'].map(c => (
          <button key={c} onClick={() => setConfidenceFilter(c)} className="btn btn-sm" style={{ background: confidenceFilter === c ? 'var(--color-teal)' : 'var(--color-surface)', color: confidenceFilter === c ? '#fff' : 'var(--color-text-mid)', border: `1px solid ${confidenceFilter === c ? 'var(--color-teal)' : 'var(--color-border)'}` }}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon">◈</div>
          <h3>No items yet</h3>
          <p>Upload documents first to extract items</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: sel ? '1fr 360px' : '1fr', gap: 14 }}>
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}><input type="checkbox" checked={selectedItems.length === filtered.length && filtered.length > 0} onChange={() => setSelectedItems(selectedItems.length === filtered.length ? [] : filtered.map(i => i.id))} style={{ cursor: 'pointer', accentColor: 'var(--color-teal)' }} /></th>
                    {['#', 'Description', 'Trade', 'Qty', 'Unit', 'Confidence'].map(h => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => {
                    const trade = item.trade_category || item.trade;
                    const tc = TRADE_COLORS[trade] || TRADE_COLORS['Other'];
                    const conf = Number(item.confidence) * (Number(item.confidence) <= 1 ? 100 : 1);
                    const isSelected = sel?.id === item.id;
                    const isLowConf = conf < 70;
                    return (
                      <tr key={item.id} style={{ background: isSelected ? 'var(--color-teal-light)' : isLowConf ? '#FFFBEB' : 'transparent', cursor: 'pointer' }}>
                        <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => toggleItem(item.id)} style={{ cursor: 'pointer', accentColor: 'var(--color-teal)' }} /></td>
                        <td onClick={() => setSel(isSelected ? null : item)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-light)' }}>{idx + 1}</td>
                        <td onClick={() => setSel(isSelected ? null : item)} style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.item_name || item.name}</td>
                        <td onClick={() => setSel(isSelected ? null : item)}><span style={{ padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 11, fontWeight: 500, color: tc.color, background: tc.bg }}>{trade}</span></td>
                        <td onClick={() => setSel(isSelected ? null : item)} style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{item.quantity}</td>
                        <td onClick={() => setSel(isSelected ? null : item)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{item.unit}</td>
                        <td onClick={() => setSel(isSelected ? null : item)}><ConfidencePill value={conf.toFixed(0)} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {sel && (
            <div className="card" style={{ animation: 'fadeIn 200ms ease' }}>
              <div className="card-header">
                <span style={{ fontWeight: 600 }}>Item Detail</span>
                <button onClick={() => setSel(null)} className="btn btn-ghost btn-sm">✕</button>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Description</div>
                  <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>{sel.item_name || sel.name}</div>
                </div>
                <div className="grid-2">
                  {[['Quantity', sel.quantity], ['Unit', sel.unit]].map(([l, v]) => (
                    <div key={l}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{l}</div>
                      <div style={{ fontFamily: 'var(--font-mono)' }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>AI Confidence</div>
                  <ConfidencePill value={(Number(sel.confidence) * (Number(sel.confidence) <= 1 ? 100 : 1)).toFixed(0)} />
                  {sel.source_page && <div style={{ fontSize: 12, color: 'var(--color-text-mid)', marginTop: 8 }}>Source: page {sel.source_page}</div>}
                </div>
                <button onClick={() => navigate('/packages')} className="btn btn-primary">Send to Package →</button>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedItems.length > 0 && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-navy)', borderRadius: 'var(--radius-md)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: 'var(--shadow-modal)', zIndex: 500 }}>
          <span style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>{selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected</span>
          <button onClick={() => navigate('/packages')} className="btn btn-primary btn-sm">Add to Package</button>
          <button onClick={() => setSelectedItems([])} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
      )}
    </div>
  );
}